import { BuildingOffice2Icon, UserIcon, CheckCircleIcon, XCircleIcon, InformationCircleIcon, DocumentTextIcon, EyeIcon, PencilSquareIcon, TrashIcon, EnvelopeIcon, PhoneIcon, DocumentPlusIcon, UserGroupIcon, PlusIcon, LinkIcon } from "@heroicons/react/24/outline";
import { SimpleOrganization } from "pages/Customers";
import { useLanguage } from "../../contexts/LanguageContext";
import { Customer } from "../../types/customer";
import SendCustomerLoginInvite from "./SendCustomerLoginInvite";
import { Tooltip } from "../common/Tooltip";
import { customerToKeyValuePairs } from '../../utils/customerDataMapper';
import { useState, useEffect } from 'react';
import { useCustomerGroups } from '../../hooks/useCustomerGroups';
import toast from 'react-hot-toast';

interface OrganizationAddButtonProps {
  customer: Customer;
  organizations: SimpleOrganization[];
  onAddOrganization: (customer: Customer, orgId: string) => void;
}

const OrganizationAddButton: React.FC<OrganizationAddButtonProps> = ({ customer, organizations, onAddOrganization }) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const { t } = useLanguage();

  const availableOrgs = organizations.filter(org => 
    !customer.organizations?.includes(org.id)
  );

  if (availableOrgs.length === 0) {
    return null;
  }

  return (
    <div className="relative">
      <button
        onClick={() => setShowDropdown(!showDropdown)}
        className="inline-flex items-center px-1.5 sm:px-2 py-0.5 rounded text-[10px] sm:text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-600 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
      >
        <PlusIcon className="w-2.5 h-2.5 sm:w-3 sm:h-3 mr-0.5 sm:mr-1" />
        <span className="hidden sm:inline">{t('customers.tooltips.addOrg')}</span>
        <span className="sm:hidden">+</span>
      </button>
      
      {showDropdown && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 z-[100]" 
            onClick={() => setShowDropdown(false)}
          />
          {/* Dropdown */}
          <div className="fixed sm:absolute left-2 right-2 sm:left-auto sm:right-auto z-[101] mt-1 sm:w-48 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg max-h-64 overflow-y-auto">
            {availableOrgs.map(org => (
              <button
                key={org.id}
                onClick={() => {
                  onAddOrganization(customer, org.id);
                  setShowDropdown(false);
                }}
                className="block w-full text-left px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 active:bg-gray-200 dark:active:bg-gray-600 first:rounded-t-md last:rounded-b-md"
              >
                <div className="flex items-center">
                  <BuildingOffice2Icon className="w-4 h-4 mr-2" />
                  {org.name}
                </div>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

// Group Add Button Component
interface GroupAddButtonProps {
  customer: Customer;
}

const GroupAddButton: React.FC<GroupAddButtonProps> = ({ customer }) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const { groups, addCustomers } = useCustomerGroups();

  const handleAddToGroup = async (groupId: string) => {
    setShowDropdown(false);
    
    // Firebase hívás - hook automatikusan frissít a háttérben
    addCustomers(groupId, [customer.id]).then(() => {
      toast.success('Ügyfél hozzáadva a csoporthoz');
    }).catch((error: any) => {
      toast.error(`Hiba: ${error.message}`);
    });
  };

  // Csak azok a csoportok, amelyekhez még nincs hozzáadva az ügyfél
  const availableGroups = groups.filter(group => 
    !group.customerIds.includes(customer.id)
  );

  if (availableGroups.length === 0) {
    return null;
  }

  return (
    <div className="relative">
      <button
        onClick={() => setShowDropdown(!showDropdown)}
        className="inline-flex items-center px-1.5 sm:px-2 py-0.5 rounded text-[10px] sm:text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-600 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
      >
        <PlusIcon className="w-2.5 h-2.5 sm:w-3 sm:h-3 mr-0.5 sm:mr-1" />
        <span className="hidden sm:inline">Csoport</span>
        <span className="sm:hidden">+</span>
      </button>
      
      {showDropdown && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 z-[100]" 
            onClick={() => setShowDropdown(false)}
          />
          {/* Dropdown */}
          <div className="fixed sm:absolute left-2 right-2 sm:left-auto sm:right-auto z-[101] mt-1 sm:w-56 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg max-h-64 overflow-y-auto">
            {availableGroups.map(group => (
              <button
                key={group.id}
                onClick={() => handleAddToGroup(group.id)}
                className="block w-full text-left px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 active:bg-gray-200 dark:active:bg-gray-600 first:rounded-t-md last:rounded-b-md"
              >
                <div className="flex items-center gap-2">
                  <div
                    className="w-6 h-6 rounded flex items-center justify-center text-sm flex-shrink-0"
                    style={{ backgroundColor: group.color || '#3B82F6' + '20' }}
                  >
                    {group.icon || '👥'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                      {group.name}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {group.customerCount} ügyfél
                    </div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

interface CustomerListProps {
  customers: Customer[];
  organizations: SimpleOrganization[];
  onStatusChange: (customer: Customer) => void;
  onViewClick: (customer: Customer) => void;
  onEditClick: (customer: Customer) => void;
  onDeleteClick: (customer: Customer) => void;
  onDocumentClick: (customer: Customer) => void;
  onGenerateDocument: (customer: Customer) => void;
  onAddOrganization: (customer: Customer, orgId: string) => void;
  onRemoveOrganization: (customer: Customer, orgId: string) => void;
  // Bulk select props
  selectedCustomerIds?: string[];
  onSelectCustomer?: (customerId: string) => void;
  onSelectAll?: () => void;
  showBulkSelect?: boolean;
}

const CustomerList: React.FC<CustomerListProps> = ({
  customers,
  organizations,
  onStatusChange,
  onViewClick,
  onEditClick,
  onDeleteClick,
  onDocumentClick,
  onGenerateDocument,
  onAddOrganization,
  onRemoveOrganization,
  selectedCustomerIds = [],
  onSelectCustomer,
  onSelectAll,
  showBulkSelect = false
}) => {
  const { t } = useLanguage();
  const { groups, removeCustomers } = useCustomerGroups();

  const handleGenerateDocument = (customer: Customer) => {
    onGenerateDocument(customer);
  };

  return (
    <>
      {/* Responsive táblázat - újratervezett, kártyákként mobil nézetben */}
      <div className="hidden lg:block bg-white dark:bg-gray-800 rounded-lg overflow-hidden shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                {showBulkSelect && (
                  <th className="px-3 py-2 w-12">
                    <input
                      type="checkbox"
                      checked={customers.length > 0 && selectedCustomerIds.length === customers.length}
                      onChange={onSelectAll}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded cursor-pointer"
                    />
                  </th>
                )}
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 dark:text-gray-300">
                  {t('customers.fields.name')}
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 dark:text-gray-300">
                  {t('customers.fields.contact')}
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 dark:text-gray-300">
                  {t('customers.fields.organizations')}
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 dark:text-gray-300">
                  Csoportok
                </th>
                <th className="px-3 py-2 text-center text-xs font-medium text-gray-600 dark:text-gray-300">
                  {t('customers.fields.status')}
                </th>
                <th className="px-3 py-2 text-center text-xs font-medium text-gray-600 dark:text-gray-300">
                  {t('customers.fields.notes')}
                </th>
                <th className="px-3 py-2 text-center text-xs font-medium text-gray-600 dark:text-gray-300 w-20">
                  {t('customers.fields.actions')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {customers.map(customer => (
                <tr key={customer.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                  {showBulkSelect && (
                    <td className="px-3 py-2 w-12">
                      <input
                        type="checkbox"
                        checked={selectedCustomerIds.includes(customer.id)}
                        onChange={() => onSelectCustomer?.(customer.id)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded cursor-pointer"
                      />
                    </td>
                  )}
                  <td className="px-3 py-2">
                    <div className="flex items-center">
                      <div className="p-1.5 rounded bg-gradient-to-br from-primary-50 to-primary-100 dark:from-primary-900/20 dark:to-primary-800/20 border-primary-200 dark:border-primary-700 border mr-2">
                        {customer.type === 'organization' ? (
                          <BuildingOffice2Icon className="h-5 w-5 text-primary-600 dark:text-primary-400" />
                        ) : (
                          <UserIcon className="h-5 w-5 text-primary-600 dark:text-primary-400" />
                        )}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <div className="text-sm font-medium text-gray-800 dark:text-gray-200">
                            {customer.type === 'organization'
                              ? customer.companyName
                              : `${customer.lastName || ''} ${customer.firstName || ''}`.trim() || 'Ismeretlen'}
                          </div>
                          {customer.registrationSource === 'public_link' && (
                            <Tooltip content={t('customers.tooltips.publicRegistration') || 'Nyilvános regisztrációs linken keresztül regisztrált'}>
                              <div className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-700">
                                <LinkIcon className="w-3 h-3 mr-1" />
                                Link
                              </div>
                            </Tooltip>
                          )}
                        </div>
                        <div className="text-xs text-gray-600 dark:text-gray-400">
                          {customer.type === 'organization' && customer.taxNumber && customer.taxNumber}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex flex-col">
                      {customer.email && (
                        <a href={`mailto:${customer.email}`} className="text-sm text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400">
                          {customer.email}
                        </a>
                      )}
                      {customer.phone && (
                        <a href={`tel:${customer.phone}`} className="text-sm text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400">
                          {customer.phone}
                        </a>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex flex-wrap gap-1">
                      {customer.organizations?.map(orgId => {
                        const org = organizations.find((o: SimpleOrganization) => o.id === orgId);
                        return org ? (
                          <div
                            key={org.id}
                            className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 border border-primary-200 dark:border-primary-700 group"
                          >
                            <BuildingOffice2Icon className="w-3 h-3 mr-1" />
                            {org.name}
                            <button
                              onClick={() => onRemoveOrganization(customer, orgId)}
                              className="ml-1 opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <XCircleIcon className="w-3 h-3 text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300" />
                            </button>
                          </div>
                        ) : null;
                      })}
                      <OrganizationAddButton 
                        customer={customer}
                        organizations={organizations}
                        onAddOrganization={onAddOrganization}
                      />
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex flex-wrap gap-1">
                      {groups.filter(group => group.customerIds.includes(customer.id)).map(group => (
                        <div
                          key={group.id}
                          className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border group"
                          style={{ 
                            backgroundColor: group.color ? group.color + '20' : '#3B82F620',
                            borderColor: group.color || '#3B82F6',
                            color: group.color || '#3B82F6'
                          }}
                        >
                          <span className="mr-1">{group.icon || '👥'}</span>
                          {group.name}
                          <button
                            onClick={() => {
                              // Firebase hívás - hook automatikusan frissít
                              removeCustomers(group.id, [customer.id]).then(() => {
                                toast.success('Ügyfél eltávolítva a csoportból');
                              }).catch((error: any) => {
                                toast.error(`Hiba: ${error.message}`);
                              });
                            }}
                            className="ml-1 opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <XCircleIcon className="w-3 h-3 text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300" />
                          </button>
                        </div>
                      ))}
                      <GroupAddButton customer={customer} />
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex justify-center">
                      <button
                        onClick={() => onStatusChange(customer)}
                        className={`p-1.5 rounded-full ${customer.status === 'active'
                          ? 'text-green-600 hover:text-green-700 hover:bg-green-50 dark:hover:bg-green-900/20'
                          : 'text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20'
                          }`}
                      >
                        {customer.status === 'active' ? (
                          <CheckCircleIcon className="h-5 w-5" />
                        ) : (
                          <XCircleIcon className="h-5 w-5" />
                        )}
                      </button>
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex justify-center">
                      {customer.notes ? (
                        <Tooltip content={customer.notes}>
                          <InformationCircleIcon
                            className="h-5 w-5 text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 cursor-help transition-colors"
                          />
                        </Tooltip>
                      ) : (
                        <InformationCircleIcon
                          className="h-5 w-5 text-gray-400 dark:text-gray-500"
                        />
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex justify-end space-x-2">
                      <SendCustomerLoginInvite customer={customer} />
                      
                      {customer.documents && customer.documents.length > 0 && (
                        <Tooltip content={t('customers.tooltips.viewDocuments') || 'View Documents'}>
                          <button
                            onClick={() => onDocumentClick(customer)}
                            className="text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400"
                          >
                            <DocumentTextIcon className="h-4 w-4" />
                          </button>
                        </Tooltip>
                      )}

                      <Tooltip content={t('customers.tooltips.view')}>
                        <button
                          onClick={() => onViewClick(customer)}
                          className="text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400"
                        >
                          <EyeIcon className="h-4 w-4" />
                        </button>
                      </Tooltip>

                      <Tooltip content={t('customers.tooltips.edit')}>
                        <button
                          onClick={() => onEditClick(customer)}
                          className="text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400"
                        >
                          <PencilSquareIcon className="h-4 w-4" />
                        </button>
                      </Tooltip>
                      <Tooltip content={t('customers.tooltips.delete')}>
                        <button
                          onClick={() => onDeleteClick(customer)}
                          className="text-gray-600 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </Tooltip>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Mobil nézet - lista/kártya elrendezés */}
      <div className="lg:hidden space-y-2 mb-2 w-full overflow-hidden">
        {customers.map(customer => (
          <div key={customer.id} className="bg-white dark:bg-gray-800 rounded-lg p-2 sm:p-3 shadow-md border border-gray-200 dark:border-gray-700 w-full overflow-hidden">
            <div className="flex justify-between w-full">
              <div className="flex items-center mr-2 mb-1.5 sm:mb-2 max-w-[60%]">
                <div className="p-1 sm:p-1.5 rounded bg-gradient-to-br from-primary-50 to-primary-100 dark:from-primary-900/20 dark:to-primary-800/20 border-primary-200 dark:border-primary-700 border mr-1 sm:mr-2 flex-shrink-0">
                  {customer.type === 'organization' ? (
                    <BuildingOffice2Icon className="h-4 w-4 sm:h-5 sm:w-5 text-primary-600 dark:text-primary-400" />
                  ) : (
                    <UserIcon className="h-4 w-4 sm:h-5 sm:w-5 text-primary-600 dark:text-primary-400" />
                  )}
                </div>
                <div className="min-w-0 w-full overflow-hidden">
                  <div className="flex items-center gap-1 overflow-hidden">
                    <div className="text-xs sm:text-sm font-medium text-gray-800 dark:text-gray-200 truncate">
                      {customer.type === 'organization'
                        ? customer.companyName
                        : `${customer.lastName || ''} ${customer.firstName || ''}`.trim() || 'Ismeretlen'}
                    </div>
                    {customer.registrationSource === 'public_link' && (
                      <Tooltip content={t('customers.tooltips.publicRegistration') || 'Nyilvános regisztrációs linken keresztül regisztrált'}>
                        <div className="inline-flex items-center px-1 py-0.5 rounded text-[10px] font-medium bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-700 flex-shrink-0">
                          <LinkIcon className="w-2.5 h-2.5 mr-0.5" />
                          <span className="hidden sm:inline">Link</span>
                        </div>
                      </Tooltip>
                    )}
                  </div>
                  <div className="text-[10px] sm:text-xs text-gray-600 dark:text-gray-400 truncate">
                    {customer.type === 'organization' && customer.taxNumber && customer.taxNumber}
                  </div>
                </div>
              </div>

              <div className="flex items-center flex-shrink-0 ml-auto space-x-0 sm:space-x-1">
                <div className="mr-2">
                  <SendCustomerLoginInvite customer={customer} />
                </div>
                
                <button
                  onClick={() => onStatusChange(customer)}
                  className={`p-1 rounded-full ${customer.status === 'active'
                    ? 'text-green-600 hover:text-green-700 hover:bg-green-50 dark:hover:bg-green-900/20'
                    : 'text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20'
                    }`}
                >
                  {customer.status === 'active' ? (
                    <CheckCircleIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                  ) : (
                    <XCircleIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                  )}
                </button>
                {customer.documents && customer.documents.length > 0 && (
                  <button
                    onClick={() => onDocumentClick(customer)}
                    className="p-1 rounded-full text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20"
                  >
                    <DocumentTextIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                  </button>
                )}
                <button
                  onClick={() => handleGenerateDocument(customer)}
                  className="p-1 rounded-full text-gray-600 dark:text-gray-400 hover:text-amber-600 dark:hover:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/20"
                >
                  <DocumentPlusIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                </button>
                <button
                  onClick={() => onEditClick(customer)}
                  className="p-1 rounded-full text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20"
                >
                  <PencilSquareIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                </button>
                <button
                  onClick={() => onDeleteClick(customer)}
                  className="p-1 rounded-full text-gray-600 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
                >
                  <TrashIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                </button>
              </div>
            </div>

            <div className="mt-1.5 sm:mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[10px] sm:text-xs overflow-hidden">
              {customer.email && (
                <a href={`mailto:${customer.email}`} className="flex items-center text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 max-w-full truncate">
                  <EnvelopeIcon className="h-3 w-3 sm:h-3.5 sm:w-3.5 mr-1 flex-shrink-0" />
                  <span className="truncate">{customer.email}</span>
                </a>
              )}
              {customer.phone && (
                <a href={`tel:${customer.phone}`} className="flex items-center text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 max-w-full truncate">
                  <PhoneIcon className="h-3 w-3 sm:h-3.5 sm:w-3.5 mr-1 flex-shrink-0" />
                  <span className="truncate">{customer.phone}</span>
                </a>
              )}
            </div>

            {/* Szervezetek, csoportok és hozzárendelő gombok */}
            <div className="mt-1.5 sm:mt-2 overflow-hidden">
              <div className="flex flex-wrap gap-0.5 sm:gap-1 overflow-hidden">
                {/* Szervezetek */}
                {customer.organizations?.map(orgId => {
                  const org = organizations.find((o: SimpleOrganization) => o.id === orgId);
                  return org ? (
                    <div
                      key={org.id}
                      className="inline-flex items-center px-1.5 sm:px-2 py-0.5 rounded text-[10px] sm:text-xs font-medium bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 border border-primary-200 dark:border-primary-700 max-w-[100px] overflow-hidden group"
                    >
                      <BuildingOffice2Icon className="w-2.5 h-2.5 sm:w-3 sm:h-3 mr-0.5 sm:mr-1 flex-shrink-0" />
                      <span className="truncate">{org.name}</span>
                      <button
                        onClick={() => onRemoveOrganization(customer, orgId)}
                        className="ml-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <XCircleIcon className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300" />
                      </button>
                    </div>
                  ) : null;
                })}
                
                {/* Csoportok */}
                {groups.filter(group => group.customerIds.includes(customer.id)).map(group => (
                  <div
                    key={group.id}
                    className="inline-flex items-center px-1.5 sm:px-2 py-0.5 rounded text-[10px] sm:text-xs font-medium border max-w-[100px] overflow-hidden group"
                    style={{ 
                      backgroundColor: group.color ? group.color + '20' : '#3B82F620',
                      borderColor: group.color || '#3B82F6',
                      color: group.color || '#3B82F6'
                    }}
                  >
                    <span className="mr-0.5 sm:mr-1 text-xs">{group.icon || '👥'}</span>
                    <span className="truncate">{group.name}</span>
                    <button
                      onClick={() => {
                        removeCustomers(group.id, [customer.id]).then(() => {
                          toast.success('Ügyfél eltávolítva a csoportból');
                        }).catch((error: any) => {
                          toast.error(`Hiba: ${error.message}`);
                        });
                      }}
                      className="ml-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <XCircleIcon className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300" />
                    </button>
                  </div>
                ))}
                
                <OrganizationAddButton 
                  customer={customer}
                  organizations={organizations}
                  onAddOrganization={onAddOrganization}
                />
                <GroupAddButton customer={customer} />
              </div>
            </div>

            {customer.notes && (
              <div className="mt-1.5 sm:mt-2 text-[10px] sm:text-xs text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 p-1.5 sm:p-2 rounded overflow-hidden">
                <div className="line-clamp-2">{customer.notes}</div>
              </div>
            )}

            <div className="mt-2 sm:mt-3 flex justify-end space-x-1 sm:space-x-2">
              <button
                onClick={() => onViewClick(customer)}
                className="text-xs sm:text-sm px-2 py-1 rounded-md bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 hover:bg-primary-100 dark:hover:bg-primary-800/30"
              >
                {t('customers.actions.view')}
              </button>
            </div>
          </div>
        ))}
      </div>
    </>
  );
};

export default CustomerList;


